import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Send } from "lucide-react";

/**
 * Editor voice-note recorder using MediaRecorder API. Records up to 30 seconds
 * of Hindi audio, uploads via `uploadFn(blob) → { url }` and calls onReady(url).
 * Also displays a live waveform of the microphone input while recording.
 */
export default function VoiceNoteRecorder({ uploadFn, onReady, disabled = false, colorClass = "text-blue-600" }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const timer = useRef(null);
  const audioBlob = useRef(null);

  const start = async () => {
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      mediaRec.current = new MediaRecorder(stream.current, { mimeType: mime });
      mediaRec.current.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mediaRec.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: mime });
        audioBlob.current = blob;
        setBlobUrl(URL.createObjectURL(blob));
        stream.current?.getTracks().forEach((t) => t.stop());
      };
      mediaRec.current.start(200);
      setRecording(true); setSeconds(0);
      timer.current = setInterval(() => setSeconds((s) => {
        const n = s + 1; if (n >= 30) stop(); return n;
      }), 1000);
    } catch (e) {
      alert("माइक्रोफ़ोन की अनुमति चाहिए");
    }
  };
  const stop = () => {
    if (mediaRec.current && mediaRec.current.state !== "inactive") mediaRec.current.stop();
    clearInterval(timer.current); setRecording(false);
  };
  const cancel = () => { setBlobUrl(""); audioBlob.current = null; setSeconds(0); };

  const send = async () => {
    if (!audioBlob.current) return;
    setUploading(true);
    try {
      const { url } = await uploadFn(audioBlob.current);
      onReady(url); cancel();
    } catch (e) {
      alert("आवाज़ अपलोड नहीं हुई");
    } finally { setUploading(false); }
  };

  if (recording) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 border border-rose-200 rounded-md" data-testid="voice-recording">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-rose-600"></span>
        </span>
        <span className="text-xs font-bold text-rose-700 tabular-nums">रिकॉर्डिंग {seconds}s / 30s</span>
        <button onClick={stop} className="ml-auto bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-3 py-1 rounded-md inline-flex items-center gap-1" data-testid="voice-stop">
          <Square className="w-3 h-3 fill-white"/>रोकें
        </button>
      </div>
    );
  }
  if (blobUrl) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="voice-preview">
        <audio src={blobUrl} controls className="h-8 flex-1 min-w-0"/>
        <button onClick={cancel} className="text-slate-500 hover:text-rose-600 p-1" title="रद्द करें" data-testid="voice-cancel"><Trash2 className="w-4 h-4"/></button>
        <button onClick={send} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1 disabled:opacity-50" data-testid="voice-send">
          <Send className="w-3 h-3"/>{uploading ? "भेज रहे…" : "भेजें"}
        </button>
      </div>
    );
  }
  return (
    <button onClick={start} disabled={disabled} className={`p-2 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40 ${colorClass}`} title="30 सेकंड की आवाज़ भेजें" data-testid="voice-record-btn">
      <Mic className="w-4 h-4"/>
    </button>
  );
}
